import { FunctionComponent, useState } from "react";
import { useTranslation } from "react-i18next";

import { InputText } from "primereact/inputtext";
import { SelectButton, SelectButtonChangeEvent } from "primereact/selectbutton";

import { StringReferenceReadModel } from "../../../../../@api";

import { translationKeys } from "../../../../models/app-translation-keys";
import { PermissionCheckboxTable } from "./PermissionCheckboxTable/PermissionCheckboxTable";
import { PermissionChipsTable } from "./PermissionChipsTable/PermissionChipsTable";
import { ISecurityGroupRoleAssignments } from "./PermissionsTab";

import "./SecurityGroupsTable.scss";

interface ISecurityGroupsTableProps {
  securityGroups: ISecurityGroupRoleAssignments[];
  roles: StringReferenceReadModel[];
  isLoading: boolean;
  onReloadTable: () => void;
}

enum TableView {
  ChipView = "ChipView",
  CheckboxView = "CheckboxView",
}

interface ITableViewOption {
  label: string;
  name: TableView;
}

const SecurityGroupsTable: FunctionComponent<ISecurityGroupsTableProps> = (props: ISecurityGroupsTableProps) => {
  const { t } = useTranslation();

  const paginatorRows = [5, 10, 20];

  // Table view options
  const tableViewOptions: ITableViewOption[] = [
    { label: translationKeys.adminArea.appRoles.views.chips, name: TableView.ChipView },
    { label: translationKeys.adminArea.appRoles.views.checkboxes, name: TableView.CheckboxView },
  ];
  const [tableView, setTableView] = useState<ITableViewOption>(tableViewOptions[0]);

  // Filters and sorting
  const [globalFilterValue, setGlobalFilterValue] = useState<string>("");

  const clearFilter = () => {
    setGlobalFilterValue("");
  };

  return (
    <div className="security-group-table">
      <div className="security-group-table-header">
        <SelectButton
          id="showAsCheckboxes"
          value={tableView}
          optionLabel="label"
          onChange={(e: SelectButtonChangeEvent) => !!e.value && setTableView(e.value)}
          options={tableViewOptions}
          itemTemplate={option => t(option.label)}
        />

        <div className="p-icon-field p-icon-field-left p-icon-field-right">
          <i className="pi pi-search p-input-icon" style={{ width: "fit-content" }} />

          <InputText
            value={globalFilterValue}
            onChange={e => {
              setGlobalFilterValue(e.target.value);
            }}
            placeholder={t(translationKeys.actions.filterTable)}
            className="pr-5"
          />

          {!!globalFilterValue?.length && (
            <i
              className="pi pi-times cursor-pointer p-link p-input-icon"
              onClick={clearFilter}
              aria-label={t(translationKeys.actions.clearFilter)}
              onKeyDown={e => e.key === "Enter" && clearFilter()}
              tabIndex={0}
              role="button"
            />
          )}
        </div>
      </div>

      {tableView.name === TableView.ChipView && (
        <PermissionChipsTable
          securityGroups={props.securityGroups}
          roles={props.roles}
          isLoading={props.isLoading}
          onReloadTable={props.onReloadTable}
          globalFilterValue={globalFilterValue}
          paginatorRows={paginatorRows}
          paginatorSelectedRows={paginatorRows[0]}
        />
      )}

      {tableView.name === TableView.CheckboxView && (
        <PermissionCheckboxTable
          securityGroups={props.securityGroups}
          roles={props.roles}
          isLoading={props.isLoading}
          onReloadTable={props.onReloadTable}
          globalFilterValue={globalFilterValue}
          paginatorRows={paginatorRows}
          paginatorSelectedRows={paginatorRows[0]}
        />
      )}
    </div>
  );
};

export default SecurityGroupsTable;
